import { computed, ref } from 'vue';
import camelCase from 'lodash.camelcase';

import { ELicenseRole } from '@/contexts/accountingContext/domain/constants';
import { setLicenseRole } from '@/contexts/accountingContext/services';
import { showErrorNotification } from '@/domains/showErrorNotification';
import redirectToInitialRoute from '@/domains/redirectToInitialRoute';
import useViewState from '@/composables/useViewState';
import { useTenantsStore } from '@/stores/tenants';
import { TPublicOffer } from '@/types';
import { useAppStore } from '@/stores/app';

import { allLicenseRoles } from '../domain/constants';

export const useSelectLicenseRole = (emit: (event: 'close', ...args: unknown[]) => void) => {
  const { isLoading, setViewStateAs } = useViewState();

  const appStore = useAppStore();
  const tenantsStore = useTenantsStore();
  const licenseSettings = computed(() => appStore.appSettings?.licenseSettings);

  const selectedLicenseRole = ref<ELicenseRole | null>(null);

  const publicOffers = computed(() => appStore.appSettings?.publicOffers || []);

  // Подумать, как избавиться от копипасты, ведь это чистая бизнес-логика, которая должна быть обособлена
  const isPublicOffersBoundToPlanCodes = computed(
    () => publicOffers.value.every((offer: TPublicOffer) => offer.planCodes.length));

  const isPublicOfferForPlanCode = (planCode: ELicenseRole) => (publicOffer: TPublicOffer) => !!publicOffer
    .planCodes
    .find((code: string) => code === planCode);

  const publicOfferForSelectedLicenseRole = computed(() => {
    if (!selectedLicenseRole.value) return null;
    if (!isPublicOffersBoundToPlanCodes.value) return null;

    return publicOffers.value.find(isPublicOfferForPlanCode(selectedLicenseRole.value));
  });

  const availableLicenseRoles = computed(() => allLicenseRoles.filter((
    role: {
      value: ELicenseRole,
      label: string,
    }) => licenseSettings.value?.[camelCase(role.value)]?.enabled));

  const updateTenantsData = () => {
    setViewStateAs.loading();

    Promise.all([tenantsStore.loadTenants(), tenantsStore.loadTenantInfo()])
      .then(() => {
        emit('close');
        redirectToInitialRoute();
      })
      .catch(() => {})
      .finally(setViewStateAs.done);
  };

  const onSubmit = () => {
    if (!selectedLicenseRole.value) return;

    setLicenseRole(selectedLicenseRole.value)
      .then(updateTenantsData)
      .catch(showErrorNotification);
  };

  return {
    isLoading,
    selectedLicenseRole,
    publicOfferForSelectedLicenseRole,
    availableLicenseRoles,

    onSubmit,
  };
};
